/**
 * API client (axios) — cookie-based auth с CSRF-защитой.
 */
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

/** Читаем csrf_token из не-httpOnly cookie */
function getCsrfToken(): string | null {
  return document.cookie.match(/csrf_token=([^;]+)/)?.[1] ?? null;
}

/** HTTP-методы, требующие CSRF-защиты */
const CSRF_METHODS = ['post', 'put', 'delete', 'patch'];

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30с — защита от бесконечного зависания запросов
  withCredentials: true, // httpOnly cookies отправляются автоматически
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — CSRF-токен для мутирующих запросов
api.interceptors.request.use((config) => {
  if (config.method && CSRF_METHODS.includes(config.method.toLowerCase())) {
    const csrf = getCsrfToken();
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

// Очередь запросов, ожидающих завершения refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: () => void;
  reject: (reason?: unknown) => void;
}> = [];

function processQueue(error: unknown | null): void {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
}

// Response interceptor — при 401 пробуем refresh, потом redirect
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Не пытаемся refresh для самого refresh/login запроса
    if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/admin/login')) {
      return Promise.reject(error);
    }

    // Если уже идёт refresh — ставим запрос в очередь
    if (isRefreshing) {
      return new Promise<void>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => api(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await api.post('/auth/refresh', {});
      processQueue(null);
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      // Не редиректим если уже на странице логина — иначе бесконечный цикл
      // (checkAuth при монтировании App → 401 → refresh fail → редирект → ремаунт → повтор)
      if (!window.location.pathname.endsWith('/login')) {
        window.location.href = '/admin/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * Загрузка изображения на сервер.
 * @param file — файл для загрузки
 * @param folder — папка на сервере ('products', 'categories' и т.д.)
 * @returns URL загруженного изображения
 */
export async function uploadImage(
  file: File,
  folder = 'products',
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post(`/admin/upload?folder=${folder}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.url;
}

export default api;

